import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, Music, UserPlus, UserCheck, AudioLines, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatCount } from "../lib/api";
import { ReelsFeed } from "../components/ReelsFeed";
import { Avatar } from "../components/Avatar";
import { useAuth } from "../context/AuthContext";

const Artist = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [artist, setArtist] = useState(null);
  const [videos, setVideos] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/artists/${id}`).then((r) => setArtist(r.data)).catch(() => setArtist(false));
    api.get(`/videos?artist_id=${id}`).then((r) => setVideos(r.data)).catch(() => setVideos([]));
  }, [id]);

  const toggleFollow = async () => {
    if (!user) { navigate("/login"); return; }
    const { data } = await api.post(`/artists/${id}/follow`);
    setArtist((a) => ({ ...a, following: data.following, followers: data.followers }));
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const { data } = await api.post("/auth/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArtist((a) => ({ ...a, avatar: data.avatar }));
      updateUser(data);
      setVideos((vs) => (vs || []).map((v) => ({ ...v, artist_avatar: data.avatar })));
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload photo");
    } finally {
      setUploadingAvatar(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (artist === null) return <div className="h-[calc(100vh-6.5rem)] flex items-center justify-center text-zinc-500">Loading…</div>;
  if (artist === false) return <div className="h-[calc(100vh-6.5rem)] flex items-center justify-center text-zinc-500">Artist not found.</div>;

  const isOwner = user && user.id === artist.id;

  return (
    <div>
      {/* Header */}
      <div className="relative h-44 overflow-hidden bg-zinc-950">
        {artist.avatar && (
          <img src={mediaUrl(artist.avatar)} alt="" className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-40" />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFD60A]/10 via-black/60 to-black" />
        <div className="relative h-full mx-auto max-w-3xl px-4 flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <Avatar src={artist.avatar} name={artist.name} className="h-24 w-24 border-2 border-[#FFD60A]" textClass="text-4xl" />
            {isOwner && (
              <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingAvatar}
                  data-testid="change-avatar-button"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 opacity-0 hover:opacity-100 transition-opacity text-white text-[10px] font-semibold uppercase tracking-wide disabled:opacity-100"
                  title="Upload profile photo"
                >
                  <Camera className="h-6 w-6 text-[#FFD60A]" />
                  {uploadingAvatar ? "Uploading…" : "Change"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
              </>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl text-white truncate" data-testid="artist-name">{artist.name}</h1>
            <p className="text-sm text-zinc-300 line-clamp-1">{artist.bio}</p>
            <div className="mt-1.5 flex items-center gap-4 text-xs text-zinc-300">
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {formatCount(artist.followers)}</span>
              <span className="flex items-center gap-1"><Music className="h-3.5 w-3.5" /> {artist.video_count}</span>
            </div>
          </div>
          {isOwner ? (
            <button onClick={() => navigate("/upload")} data-testid="owner-upload-button" className="bg-[#FFD60A] text-black px-5 py-2.5 font-semibold hover:bg-[#FFD60A]/90 transition-colors">Upload</button>
          ) : (
            <button onClick={toggleFollow} data-testid="follow-button" className={`flex items-center gap-2 px-5 py-2.5 font-semibold transition-colors ${artist.following ? "bg-white/10 border border-white/20 text-white" : "bg-[#FFD60A] text-black hover:bg-[#FFD60A]/90"}`}>
              {artist.following ? <><UserCheck className="h-4 w-4" /> Following</> : <><UserPlus className="h-4 w-4" /> Follow</>}
            </button>
          )}
        </div>
      </div>

      {/* Vertical feed of this artist's videos */}
      {videos === null ? (
        <div className="h-[calc(100vh-6.5rem-11rem)] flex items-center justify-center text-zinc-500">Loading videos…</div>
      ) : (
        <ReelsFeed videos={videos} heightClass="h-[calc(100vh-6.5rem-11rem)]" emptyText="No videos uploaded yet." />
      )}
    </div>
  );
};

export default Artist;
